import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { LineChart as LineIcon, TrendingUp, TrendingDown } from "lucide-react";
import { CLP } from "@/lib/format";
import { Card } from "@/components/ui/card";

const MONTHS = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
const label = (k) => `${MONTHS[Number(k.slice(5, 7)) - 1]} ${k.slice(2, 4)}`;
const short = (v) => (v >= 1e6 ? `$${(v / 1e6).toLocaleString("es-CL", { maximumFractionDigits: 1 })}M` : v >= 1e3 ? `$${Math.round(v / 1e3)}k` : `$${v}`);

const Tip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="rounded-lg border bg-popover px-3 py-2 text-xs shadow-md">
      <p className="font-semibold capitalize">{label(d.month)}</p>
      <p className="text-primary font-bold">{CLP(d.est_clp)} estimado</p>
      <p className="text-muted-foreground">{d.students} alumno{d.students === 1 ? "" : "s"} · {d.count} reserva{d.count === 1 ? "" : "s"}</p>
    </div>
  );
};

const Trend = ({ data }) => {
  const [prev, last] = data.slice(-2).map((d) => d.est_clp);
  if (data.length < 2 || !prev) return null;
  const pct = Math.round(((last - prev) / prev) * 100);
  const up = pct >= 0;
  const Icon = up ? TrendingUp : TrendingDown;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-semibold ${up ? "text-emerald-600" : "text-destructive"}`} data-testid="income-trend">
      <Icon className="w-3.5 h-3.5" />{up ? "+" : ""}{pct}% vs mes anterior
    </span>
  );
};

export function IncomeChart({ data = [] }) {
  const empty = !data.some((d) => d.est_clp);
  return (
    <Card className="p-5" data-testid="income-chart">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <h3 className="font-bold flex items-center gap-2"><LineIcon className="w-5 h-5 text-primary" />Evolución de ingresos estimados</h3>
        <Trend data={data} />
      </div>
      {empty ? (
        <p className="text-sm text-muted-foreground py-10 text-center" data-testid="income-chart-empty">Aún no hay reservas en este período. Cuando tus alumnos reserven, aquí verás cómo crecen tus talleres.</p>
      ) : (
        <div className="h-64 -ml-2">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="estFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid vertical={false} stroke="hsl(var(--border))" />
              <XAxis dataKey="month" tickFormatter={label} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
              <YAxis tickFormatter={short} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} width={48} />
              <Tooltip content={<Tip />} cursor={{ stroke: "hsl(var(--primary) / 0.3)" }} />
              <Area type="monotone" dataKey="est_clp" stroke="hsl(var(--primary))" strokeWidth={2.5} fill="url(#estFill)" dot={{ r: 3, fill: "hsl(var(--primary))" }} activeDot={{ r: 5 }} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
      <p className="text-xs text-muted-foreground mt-3">Ingreso estimado = precio del taller al día de la reserva × cupos, agrupado por mes de reserva. Sin fechas elegidas se muestran los últimos 12 meses.</p>
    </Card>
  );
}
